import React from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay, isToday, parseISO } from "date-fns";
import type { BoardState, Task } from "./types";

const PRIORITY_COLORS: Record<string, string> = {
  low: "bg-secondary text-secondary-foreground border-border",
  medium: "bg-info/20 text-info border-info/20",
  high: "bg-warning/20 text-warning border-warning/20",
  critical: "bg-destructive/20 text-destructive border-destructive/20",
};

const PRIORITY_DOT: Record<string, string> = {
  low: "bg-secondary",
  medium: "bg-info",
  high: "bg-warning",
  critical: "bg-destructive",
};

interface BoardCalendarViewProps {
  state: BoardState;
  onTaskClick: (task: Task) => void;
  onTaskUpdate?: (taskId: string, updates: Partial<Task>) => void;
  onCreateTask?: (date: Date) => void;
  searchTerm?: string;
}

export const BoardCalendarView: React.FC<BoardCalendarViewProps> = ({ state, onTaskClick, onTaskUpdate, onCreateTask, searchTerm }) => {
  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [draggedTask, setDraggedTask] = React.useState<Task | null>(null);
  const [hoveredDate, setHoveredDate] = React.useState<Date | null>(null);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const allTasks = React.useMemo(() => {
    return Object.values(state.tasksById).filter(t => {
      if (state.archived.includes(t.id)) return false;
      if (searchTerm?.trim()) {
        const term = searchTerm.toLowerCase();
        return (
          t.title?.toLowerCase().includes(term) ||
          t.description?.toLowerCase().includes(term) ||
          t.assignees?.some(a => a.name.toLowerCase().includes(term))
        );
      }
      return true;
    });
  }, [state.tasksById, state.archived, searchTerm]);

  const getTasksForDay = (day: Date): Task[] => {
    return allTasks.filter(task => {
      if (task.dueDate) {
        return isSameDay(parseISO(task.dueDate), day);
      }
      return false;
    });
  };

  const weeks: Date[][] = [];
  let current = calStart;
  while (current <= calEnd) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(current);
      current = addDays(current, 1);
    }
    weeks.push(week);
  }

  const handleDragStart = (e: React.DragEvent, task: Task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetDate: Date) => {
    e.preventDefault();
    if (draggedTask && onTaskUpdate) {
      const newDueDate = format(targetDate, "yyyy-MM-dd");
      onTaskUpdate(draggedTask.id, { dueDate: newDueDate });
    }
    setDraggedTask(null);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
  };



  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">
          {format(currentDate, "MMMM yyyy")}
        </h3>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentDate(subMonths(currentDate, 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-8 px-3 text-xs" onClick={() => setCurrentDate(new Date())}>
            Today
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentDate(addMonths(currentDate, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border overflow-hidden bg-board-column">
        <div className="grid grid-cols-7 border-b border-border">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
            <div key={day} className="py-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {weeks.flatMap((week, wi) =>
            week.map((day, di) => {
              const tasks = getTasksForDay(day);
              const isCurrentMonth = isSameMonth(day, currentDate);
              const isCurrentDay = isToday(day);
              const isLast = wi === weeks.length - 1;

              return (
                <div
                  key={`${wi}-${di}`}
                  className={`min-h-[100px] p-1.5 border-b border-r border-border/40 relative group ${
                    !isLast && di === 6 ? "" : ""
                  } ${isLast ? "border-b-0" : ""} ${di === 6 ? "border-r-0" : ""}`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, day)}
                  onMouseEnter={() => setHoveredDate(day)}
                  onMouseLeave={() => setHoveredDate(null)}
                >
                  <div className="flex items-center justify-end mb-1">
                    <span
                      className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full transition-colors ${
                        isCurrentDay
                          ? "bg-primary text-primary-foreground font-bold"
                          : isCurrentMonth
                          ? "text-foreground"
                          : "text-muted-foreground/30"
                      }`}
                    >
                      {format(day, "d")}
                    </span>
                  </div>

                  {/* Hover indicator for creating new task */}
                  {hoveredDate && isSameDay(hoveredDate, day) && onCreateTask && (
                    <button
                      onClick={() => onCreateTask(day)}
                      className="absolute top-1.5 left-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground bg-accent/50 hover:bg-accent px-1.5 py-0.5 rounded border border-border/50"
                    >
                      <Plus className="w-2.5 h-2.5" />
                      <span>Add task</span>
                    </button>
                  )}

                  <div className="flex flex-col gap-0.5">
                    {tasks.slice(0, 3).map(task => (
                      <button
                        key={task.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, task)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onTaskClick(task)}
                        className={`w-full text-left text-xs px-1.5 py-0.5 rounded border truncate flex items-center gap-1 hover:opacity-80 transition-opacity cursor-move ${
                          draggedTask?.id === task.id ? "opacity-50" : ""
                        } ${task.completed ? "bg-green-500/20 text-green-700 dark:text-green-300 border-green-500/20" : PRIORITY_COLORS[task.priority] || "bg-secondary text-secondary-foreground border-border"}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${task.completed ? "bg-green-500" : PRIORITY_DOT[task.priority] || "bg-secondary"}`} />
                        <span className="truncate">{task.title}</span>
                      </button>
                    ))}
                    {tasks.length > 3 && (
                      <span className="text-xs text-muted-foreground/60 px-1">+{tasks.length - 3} more</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="font-medium">Priority:</span>
        {(["low", "medium", "high", "critical"] as const).map(p => (
          <span key={p} className="flex items-center gap-1 capitalize">
            <span className={`w-2 h-2 rounded-full ${PRIORITY_DOT[p]}`} />
            {p}
          </span>
        ))}
        <span className="ml-auto text-gray-400 dark:text-gray-500">
          {allTasks.filter(t => t.dueDate).length} tasks with due dates
        </span>
      </div>
    </div>
  );
};
